import axios from 'axios';
import {
  EvaluationCommentRuleValue,
  EvaluationContextType,
  EvaluationContextTypeValue,
  EvaluationErrorKey,
  EvaluationOverviewFilterValue,
  EvaluationOverviewSortValue,
  EvaluationQuestionTypeValue,
  EvaluationRecipientStatusValue,
  EvaluationRequestStatusValue,
  EvaluationSortDirectionValue,
  EvaluationSourceValue,
} from '../util/evaluationContract';
import { clearEvaluationsCache } from '../util/evaluationsCache';

export type RequestTag = { _id: string; name: string; archived: boolean };

export type EvaluationTagDto = { _id: string; name: string; archived: boolean };

export type SupplierSummary = {
  supplier: { _id: string; name: string; isDeleted: boolean };
  score: number | null;
  evaluationCount: number;
  evaluatedProjectsCount: number;
  lastEvaluatedAt: string | null;
  trend: { score: number; submittedAt: string }[];
  byTag: { tagId: string; name: string; archived: boolean; score: number; count: number }[];
  untagged: { score: number | null; count: number };
  comments: {
    responseId: string;
    requestId: string;
    questionText: string;
    score: number | null;
    skipped: boolean;
    comment: string;
    respondentName: string;
    projectName: string | null;
    submittedAt: string;
  }[];
  requests: {
    requestId: string;
    title: string;
    contextType: EvaluationContextTypeValue;
    projectName: string | null;
    projectNumber: string | null;
    sentDate: string;
    dueDate: string;
    status: EvaluationRequestStatusValue;
    sentCount: number;
    answeredCount: number;
    score: number | null;
    tags: RequestTag[];
  }[];
  canHide: boolean;
};

export type ResponseDetail = {
  responseId: string;
  requestId: string;
  title: string;
  supplierName: string;
  projectName: string | null;
  respondent: { name: string; email: string; title: string | null };
  submittedAt: string;
  late: boolean;
  score: number | null;
  hidden: { at: string; reason: string } | null;
  answers: {
    key: string;
    text: string;
    type: EvaluationQuestionTypeValue;
    score: number | null;
    skipped: boolean;
    comment: string | null;
    textAnswer: string | null;
  }[];
  canHide: boolean;
};

export type RequestStatus = {
  request: {
    _id: string;
    title: string;
    supplier: { _id: string; name: string; organizationNumber: string | null };
    context: {
      type: EvaluationContextTypeValue;
      projectId: string | null;
      projectName: string | null;
      projectNumber: string | null;
      areaName: string | null;
    };
    dueDate: string;
    status: EvaluationRequestStatusValue;
    createdAt: string;
    senderName: string | null;
    /** The person whose own row gets no email; null fields on a migrated utvärdering. */
    sender: { userId: string | null; email: string | null; name: string | null };
    source: EvaluationSourceValue;
    templateName: string | null;
    closedAt: string | null;
    cancelledAt: string | null;
    completedAt: string | null;
    tags: RequestTag[];
  };
  tiles: {
    recipients: number;
    answered: number;
    declined: number;
    waiting: number;
    failed: number;
    notSent: number;
    overdue: number;
  };
  recipients: {
    _id: string;
    name: string;
    email: string | null;
    title: string | null;
    status: EvaluationRecipientStatusValue;
    sentAt: string | null;
    openedAt: string | null;
    completedAt: string | null;
    declinedAt: string | null;
    responseId: string | null;
    late: boolean;
    score: number | null;
    userId: string | null;
    isSelf: boolean;
    canAnswerHere: boolean;
    hasLink: boolean;
  }[];
  canHide: boolean;
  canManage: boolean;
  canEditTags: boolean;
  canCancel: boolean;
  canReopen: boolean;
  viewerIsSender: boolean;
};

export type RecipientInput = { userId?: string; email?: string; name?: string };

export type SendOutcome = {
  sent: number;
  failed: { recipientId: string; email: string; reason: string }[];
  skipped: { email: string; reason: EvaluationErrorKey }[];
};

export type ManageResult = { status: RequestStatus; outcome: SendOutcome | null };

export type ReopenResult = ManageResult & {
  revoked: { duplicates: number; formerMembers: number };
};

export type BatchOutcome = {
  batchId: string;
  requestIds: string[];
  sent: number;
  failed: { supplierId: string | null; email: string | null; reason: string }[];
  skipped: { supplierId: string | null; email: string | null; reason: string }[];
  inProgress?: boolean;
};

export type HistoricalSuggestion = {
  key: string;
  name: string | null;
  number: string | null;
  count: number;
};

export type StartEvaluationContext =
  | { type: typeof EvaluationContextType.Project; projectId: string }
  | {
      type: typeof EvaluationContextType.Historical;
      historical: { name: string; number: string };
    }
  | { type: typeof EvaluationContextType.None };

export type RequestRow = SupplierSummary['requests'][number];

export type OverviewCounts = {
  all: number;
  ongoing: number;
  overdue: number;
  finished: number;
  unevaluated: number;
};

export type OverviewRequestRow = RequestRow & {
  supplierId: string;
  projectId: string | null;
  areaId: string | null;
  areaName: string | null;
  historicalName: string | null;
  batchId: string | null;
};

export type WinnerPurchase = { areaId: string; name: string; endDay: string | null };

export type UnevaluatedPairLine = {
  projectId: string;
  purchases: WinnerPurchase[];
};

export type ProjectGroupSupplier = {
  supplierId: string;
  name: string;
  isDeleted: boolean;
  isBlocked: boolean;
  /** The project's purchases this supplier sits in. */
  purchases: { areaId: string; name: string }[];
  unevaluated: UnevaluatedPairLine | null;
  /** Matching rows only. */
  requests: OverviewRequestRow[];
};

export type RowsCap = { shown: number; total: number };

export type ProjectGroup = {
  /** The contextKey: 'project:<id>' | 'historical:<key>' | 'none'. */
  key: string;
  type: EvaluationContextTypeValue;
  projectId: string | null;
  name: string | null;
  number: string | null;
  isDeleted: boolean;
  ownOnly: boolean;
  canStart: boolean;
  /** Header numbers summarise the whole visible group, whatever the filters. */
  supplierCount: number;
  activeCount: number;
  toEvaluateCount: number;
  score: number | null;
  lastActivityDay: string;
  rowsCapped: RowsCap | null;
  suppliers: ProjectGroupSupplier[];
};

export type OverviewBatch = { batchId: string; title: string | null; createdDay: string };

export type ByProjectPage = {
  page: number;
  pageSize: number;
  totalCount: number;
  counts: OverviewCounts;
  batch: OverviewBatch | null;
  groups: ProjectGroup[];
};

export type SupplierOverviewRow = {
  supplierId: string;
  name: string;
  isDeleted: boolean;
  isBlocked: boolean;
  score: number | null;
  evaluationCount: number;
  activeCount: number;
  lastEvaluatedDay: string | null;
  unevaluated: {
    projectId: string;
    projectName: string;
    projectNumber: string | null;
    canStart: boolean;
    purchases: WinnerPurchase[];
  }[];
  /** Active and overdue, due date ascending. */
  ongoing: OverviewRequestRow[];
  /** Completed, closed (and cancelled under Alla), newest first. */
  finished: OverviewRequestRow[];
  rowsCapped: { ongoing: RowsCap | null; finished: RowsCap | null };
};

export type BySupplierPage = {
  page: number;
  pageSize: number;
  totalCount: number;
  counts: OverviewCounts;
  batch: OverviewBatch | null;
  focus: { supplierId: string; name: string } | null;
  suppliers: SupplierOverviewRow[];
};

/** A tile is null when it failed; the UI shows '–' then, never 0. */
export type OverviewMetrics = {
  ongoing: number | null;
  waiting: number | null;
  waitingRequests: number | null;
  overdue: number | null;
  completedThisMonth: number | null;
  /** 'YYYY-MM', the Stockholm month. */
  month: string;
};

export type OverviewStatus = Exclude<EvaluationOverviewFilterValue, 'all'>;

export type OverviewApiQuery = {
  /** None means every status. */
  statuses?: OverviewStatus[];
  tagIds?: string[];
  q?: string;
  batchId?: string;
  /** 0-based; the URL's page is 1-based. */
  page: number;
  /** Sent by getBySupplier only. */
  supplierId?: string;
  sort?: EvaluationOverviewSortValue;
  dir?: EvaluationSortDirectionValue;
};

export type TemplateQuestionSummary = {
  key: string;
  text: string;
  type: EvaluationQuestionTypeValue;
  required: boolean;
  allowSkip: boolean;
  commentRule: EvaluationCommentRuleValue;
};

export type TemplateSummary = {
  _id: string;
  name: string;
  description: string | null;
  /** Paragraphs excluded ("N frågor"). */
  questionCount: number;
  /** Every question, paragraphs included; its length is the question cap's basis. */
  questions: TemplateQuestionSummary[];
  /** Active tags only. */
  suggestedTagIds: string[];
};

export type WizardProject = {
  _id: string;
  name: string;
  number: string | null;
  isOpen: boolean;
  areas: {
    areaId: string;
    name: string;
    supplierIds: string[];
    /** The live register suppliers rewarded this purchase. */
    winners: { supplierId: string; name: string; evaluated: boolean }[];
  }[];
};

export type SupplierOption = {
  _id: string;
  name: string;
  organizationNumber: string | null;
  areaNames: string[];
  isBlocked: boolean;
  score: number | null;
  evaluationCount: number;
};

export type WizardBody = {
  suppliers: { supplierId: string; areaId: string | null }[];
  context: StartEvaluationContext;
  /** Without one the questions are only the picked questionIds ("Egna frågor"). */
  templateId?: string;
  /** Ids from the org's erfa question list: extras on a template, or the whole Egna frågor set. */
  questionIds: string[];
  /** Template question keys left out of this send; the template itself never changes. */
  removedQuestionKeys: string[];
  /** Per question key, template or `added-<questionId>`: required in this send or not. */
  requiredByKey: Record<string, boolean>;
  /** Per rating question key, as requiredByKey: whether Vet ej may be answered in this send. */
  allowSkipByKey: Record<string, boolean>;
  tagIds: string[];
  recipients: RecipientInput[];
  dueDayKey: string;
  title: string;
  message: string;
  idempotencyKey: string;
};

const isEmptyParam = (value: unknown) =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length);

// Lists go as "a,b"; empty values are left out so the server's defaults apply.
const overviewParams = ({ statuses, tagIds, ...rest }: OverviewApiQuery) =>
  Object.fromEntries(
    Object.entries({ ...rest, status: statuses?.join(','), tagIds: tagIds?.join(',') }).filter(
      ([, value]) => !isEmptyParam(value),
    ),
  );

// The bare path: axiosHelper rewrites /api/... to /api/organizations/<slug>/....
const BASE_URL = '/api/evaluations';

// Any write may change what a cached read answered, so the cache starts over even when it fails.
const post = async <T>(path: string, body: unknown): Promise<T> => {
  try {
    const response = await axios.post(`${BASE_URL}${path}`, body);
    return response.data;
  } finally {
    clearEvaluationsCache();
  }
};

class ServiceEvaluations {
  async getSupplierSummary(supplierId: string): Promise<SupplierSummary> {
    const response = await axios.get(`${BASE_URL}/suppliers/${supplierId}/summary`);
    return response.data;
  }

  async getResponse(responseId: string): Promise<ResponseDetail> {
    const response = await axios.get(`${BASE_URL}/responses/${responseId}`);
    return response.data;
  }

  async hideResponse(responseId: string, reason: string): Promise<void> {
    await post(`/responses/${responseId}/hide`, { reason });
  }

  async getRequestStatus(requestId: string): Promise<RequestStatus> {
    const response = await axios.get(`${BASE_URL}/requests/${requestId}`);
    return response.data;
  }

  async closeRequest(requestId: string): Promise<ManageResult> {
    return post(`/requests/${requestId}/close`, {});
  }

  async reopenRequest(requestId: string, dueDayKey?: string): Promise<ReopenResult> {
    return post(`/requests/${requestId}/reopen`, dueDayKey ? { dueDayKey } : {});
  }

  async changeDueDate(requestId: string, dueDayKey: string): Promise<ManageResult> {
    return post(`/requests/${requestId}/due-date`, { dueDayKey });
  }

  async cancelRequest(requestId: string): Promise<ManageResult> {
    return post(`/requests/${requestId}/cancel`, {});
  }

  async remindPending(requestId: string): Promise<ManageResult> {
    return post(`/requests/${requestId}/remind`, {});
  }

  async addRecipients(requestId: string, recipients: RecipientInput[]): Promise<ManageResult> {
    return post(`/requests/${requestId}/recipients`, {
      recipients,
    });
  }

  async resendRecipient(requestId: string, recipientId: string): Promise<ManageResult> {
    return post(`/requests/${requestId}/recipients/${recipientId}/resend`, {});
  }

  async changeRecipientEmail(
    requestId: string,
    recipientId: string,
    email: string,
  ): Promise<ManageResult> {
    return post(`/requests/${requestId}/recipients/${recipientId}/email`, { email });
  }

  async removeRecipient(requestId: string, recipientId: string): Promise<ManageResult> {
    return post(`/requests/${requestId}/recipients/${recipientId}/remove`, {});
  }

  async copyRecipientLink(requestId: string, recipientId: string): Promise<{ link: string }> {
    return post(`/requests/${requestId}/recipients/${recipientId}/link`, {});
  }

  async getAnswerPath(requestId: string, recipientId: string): Promise<{ path: string }> {
    return post(`/requests/${requestId}/recipients/${recipientId}/answer-link`, {});
  }

  async updateRequestTags(requestId: string, tagIds: string[]): Promise<ManageResult> {
    return post(`/requests/${requestId}/tags`, { tagIds });
  }

  async getHistoricalSuggestions(q: string): Promise<HistoricalSuggestion[]> {
    const response = await axios.get(`${BASE_URL}/historical-contexts`, { params: { q } });
    return response.data;
  }

  async getByProject(query: OverviewApiQuery): Promise<ByProjectPage> {
    const params = overviewParams({ ...query, supplierId: undefined });
    const response = await axios.get(`${BASE_URL}/by-project`, { params });
    return response.data;
  }

  async getBySupplier(query: OverviewApiQuery): Promise<BySupplierPage> {
    const response = await axios.get(`${BASE_URL}/by-supplier`, { params: overviewParams(query) });
    return response.data;
  }

  async getMetrics(): Promise<OverviewMetrics> {
    const response = await axios.get(`${BASE_URL}/metrics`);
    return response.data;
  }

  async getTemplates(): Promise<TemplateSummary[]> {
    const response = await axios.get(`${BASE_URL}/templates`);
    return response.data;
  }

  async getWizardProjects(search: { q?: string; id?: string }): Promise<WizardProject[]> {
    const params = Object.fromEntries(
      Object.entries(search).filter(([, value]) => !isEmptyParam(value)),
    );
    const response = await axios.get(`${BASE_URL}/projects`, { params });
    return response.data;
  }

  async getSupplierOptions(
    q: string,
    page: number,
  ): Promise<{ suppliers: SupplierOption[]; hasMore: boolean }> {
    const response = await axios.get(`${BASE_URL}/supplier-options`, { params: { q, page } });
    return response.data;
  }

  async startWizard(body: WizardBody): Promise<BatchOutcome> {
    return post('/wizard', body);
  }

  async getTags(): Promise<EvaluationTagDto[]> {
    const response = await axios.get(`${BASE_URL}/tags`);
    return response.data;
  }

  async addOrFindTag(name: string): Promise<EvaluationTagDto> {
    return post('/tags', { name });
  }

  async renameTag(tagId: string, name: string): Promise<EvaluationTagDto> {
    return post(`/tags/${tagId}/rename`, { name });
  }

  async archiveTag(tagId: string): Promise<EvaluationTagDto> {
    return post(`/tags/${tagId}/archive`, {});
  }

  async unarchiveTag(tagId: string): Promise<EvaluationTagDto> {
    return post(`/tags/${tagId}/unarchive`, {});
  }
}

export const serviceEvaluations = new ServiceEvaluations();
